import DOMPurify from 'dompurify';
import { useSingleService } from './SingleServiceStore';

const styles = {
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        fontWeight: 500,
        padding: '3px 10px',
        borderRadius: 20,
    },
    badgePurple: { background: '#EEEDFE', color: '#3C3489' },
    badgeTeal:   { background: '#E1F5EE', color: '#0F6E56' },
    badgeBlue:   { background: '#E6F1FB', color: '#185FA5' },
    badgeAmber:  { background: '#FAEEDA', color: '#854F0B' },
    badgeGreen:  { background: '#EAF3DE', color: '#3B6D11' },

    card: {
        background: 'var(--qk-card-bg)',
        border: '0.5px solid rgba(0,0,0,0.12)',
        borderRadius: 12,
        padding: '18px 20px',
        marginBottom: 14,
    },
    divider: {
        border: 'none',
        borderTop: '0.5px solid rgba(0,0,0,0.1)',
        margin: '16px 0',
    },
    statGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 10,
    },
    statCard: {
        background: 'var(--qk-bg-soft)',
        borderRadius: 8,
        padding: '12px 14px',
    },
    statLabel: {
        fontSize: 11,
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    statValue: {
        fontSize: 20,
        fontWeight: 500,
        color: '#1a1a1a',
    },
    infoRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 0',
        borderBottom: '0.5px solid rgba(0,0,0,0.08)',
        fontSize: 13,
    },
    infoLabel: {
        color: '#888',
        minWidth: 120,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    infoValue: {
        color: '#1a1a1a',
        fontWeight: 500,
    },
    hallCard: {
        background: 'var(--qk-card-bg)',
        border: '0.5px solid rgba(0,0,0,0.12)',
        borderRadius: 12,
        overflow: 'hidden',
    },
    hallHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 14px',
        borderBottom: '0.5px solid rgba(0,0,0,0.08)',
    },
    hallBody: { padding: '12px 14px' },
    eventRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '7px 0',
        borderBottom: '0.5px solid rgba(0,0,0,0.06)',
        fontSize: 13,
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 500,
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    bizCard: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: 'var(--qk-bg-soft)',
        borderRadius: 8,
        padding: '12px 14px',
        border: '1px solid rgba(0,0,0,0.05)',
    },
};

function Badge({ variant = 'purple', icon, children }) {
    const variantStyle = {
        purple: styles.badgePurple,
        teal:   styles.badgeTeal,
        blue:   styles.badgeBlue,
        amber:  styles.badgeAmber,
        green:  styles.badgeGreen,
    }[variant];
    return (
        <span style={{ ...styles.badge, ...variantStyle }}>
            {icon && <i className={`ti ${icon}`} style={{ fontSize: 11 }} aria-hidden="true" />}
            {children}
        </span>
    );
}

function StatCard({ icon, label, value }) {
    return (
        <div style={styles.statCard}>
            <div style={styles.statLabel}>
                <i className={`ti ${icon}`} style={{ fontSize: 11 }} aria-hidden="true" />
                {label}
            </div>
            <div style={styles.statValue}>{value}</div>
        </div>
    );
}

function InfoRow({ icon, label, children }) {
    return (
        <div style={styles.infoRow}>
            <span style={styles.infoLabel}>
                <i className={`ti ${icon}`} style={{ fontSize: 14 }} aria-hidden="true" />
                {label}
            </span>
            <span style={styles.infoValue}>{children}</span>
        </div>
    );
}

function HallCard({ hall }) {
    const totalCapacity = hall.places?.toLocaleString() ?? '—';
    return (
        <div style={styles.hallCard}>
            <div style={styles.hallHeader}>
                <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a' }}>{hall.name}</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                        {hall.area ? `${hall.area.toLocaleString()} m²` : ''}{hall.places ? ` · ${hall.places.toLocaleString()} o'rin` : ''}
                    </div>
                </div>
                <Badge variant="blue">ID: {hall.id}</Badge>
            </div>
            <div style={styles.hallBody}>
                <div style={{ fontSize: 11, fontWeight: 500, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                    Tadbirlar narxi
                </div>
                {hall.event_types?.length > 0 ? (
                    hall.event_types.map((evt, idx) => (
                        <div
                            key={evt.id}
                            style={{
                                ...styles.eventRow,
                                ...(idx === hall.event_types.length - 1 ? { borderBottom: 'none' } : {}),
                            }}
                        >
                            <span style={{ color: '#1a1a1a' }}>{evt.name}</span>
                            <Badge variant="teal">
                                {Number(evt.price).toLocaleString()} so'm
                            </Badge>
                        </div>
                    ))
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 0', color: '#aaa', fontSize: 13 }}>
                        <i className="ti ti-info-circle" style={{ fontSize: 15 }} aria-hidden="true" />
                        Hozircha tadbirlar yo'q
                    </div>
                )}
            </div>
        </div>
    );
}

function Spinner() {
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <i className="ti ti-loader-2" style={{ fontSize: 32, color: '#534AB7', animation: 'spin 1s linear infinite' }} />
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

function Empty() {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 180, gap: 10, color: '#aaa' }}>
            <i className="ti ti-mood-empty" style={{ fontSize: 40 }} aria-hidden="true" />
            <span style={{ fontSize: 14 }}>Xizmat topilmadi</span>
        </div>
    );
}

function ServiceInfo() {
    const { service, loading } = useSingleService();

    if (loading) return <Spinner />;
    if (!service) return <Empty />;

    const totalCapacity = (service.halls || []).reduce((sum, h) => sum + (h.places || 0), 0);
    const totalEvents   = (service.halls || []).reduce((sum, h) => sum + (h.event_types?.length || 0), 0);
    const createdDate   = service.created_at
        ? new Date(service.created_at).toLocaleDateString('uz-UZ', { day: 'numeric', month: 'long', year: 'numeric' })
        : '—';

    return (
        <div style={{ padding: '4px 0 20px', fontFamily: 'inherit' }}>

            {/* --- Header card --- */}
            <div style={styles.card}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    <div style={{
                        width: 72, height: 72, borderRadius: 12,
                        background: '#EEEDFE', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <i className="ti ti-building-community" style={{ fontSize: 32, color: '#534AB7' }} aria-hidden="true" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                            <span style={{ fontSize: 20, fontWeight: 500, color: '#1a1a1a' }}>{service.name}</span>
                            {service.type && <Badge variant="purple" icon="ti-confetti">{service.type}</Badge>}
                            <Badge variant="teal">ID: {service.id}</Badge>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#888', marginBottom: 4 }}>
                            <i className="ti ti-map-pin" style={{ fontSize: 14 }} aria-hidden="true" />
                            <span>{service.region}{service.district ? `, ${service.district}` : ''}</span>
                        </div>
                        {service.address && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#888' }}>
                                <i className="ti ti-home" style={{ fontSize: 14 }} aria-hidden="true" />
                                <span>{service.address}</span>
                            </div>
                        )}
                    </div>
                </div>

                <hr style={styles.divider} />

                <div style={styles.statGrid}>
                    <StatCard icon="ti-door" label="Zallar" value={(service.halls?.length || 0)} />
                    <StatCard icon="ti-users" label="Jami sig'im" value={totalCapacity.toLocaleString()} />
                    <StatCard icon="ti-calendar-event" label="Tadbirlar" value={totalEvents} />
                </div>

                <hr style={styles.divider} />

                <div>
                    {service.lat && service.long && (
                        <InfoRow icon="ti-map-2" label="Koordinatalar">
                            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                {service.lat}°N, {service.long}°E
                            </span>
                        </InfoRow>
                    )}
                    <InfoRow icon="ti-calendar" label="Yaratilgan">
                        {createdDate}
                    </InfoRow>
                </div>

                {service.desc && (
                    <>
                        <hr style={styles.divider} />
                        <div style={{ background: 'var(--qk-bg-soft)', borderRadius: 8, padding: '12px 14px' }}>
                            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Tavsif</div>
                            <div
                                style={{ fontSize: 14, color: '#1a1a1a', lineHeight: 1.6 }}
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(service.desc) }}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* --- Halls --- */}
            {service.halls?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                    <div style={styles.sectionTitle}>
                        <i className="ti ti-door" aria-hidden="true" />
                        Zallar
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {service.halls.map(hall => (
                            <HallCard key={hall.id} hall={hall} />
                        ))}
                    </div>
                </div>
            )}

            {/* --- Business --- */}
            {service.business && (
                <div>
                    <div style={styles.sectionTitle}>
                        <i className="ti ti-building" aria-hidden="true" />
                        Biznes ma'lumoti
                    </div>
                    <div style={styles.bizCard}>
                        <div style={{
                            width: 44, height: 44, borderRadius: 8,
                            background: '#E6F1FB', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <i className="ti ti-building-skyscraper" style={{ fontSize: 22, color: '#185FA5' }} aria-hidden="true" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a' }}>{service.business.name}</div>
                            {service.business.address && (
                                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{service.business.address}</div>
                            )}
                            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                                {service.business.phone && (
                                    <Badge variant="amber" icon="ti-phone">{service.business.phone}</Badge>
                                )}
                                {service.business.stir && (
                                    <Badge variant="amber" icon="ti-id">STIR: {service.business.stir}</Badge>
                                )}
                            </div>
                        </div>
                        {service.business.status === 1 && (
                            <Badge variant="green" icon="ti-check">Faol</Badge>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ServiceInfo;
